"use client";

import Image from "next/image";
import { useLocale, useTranslations } from "next-intl";
import { Link } from "@/i18n/navigation";
import { Badge } from "@/components/ui/badge";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";
import { formatCourseDuration } from "@/lib/courseDuration";

/**
 * Carte formation partagée — source de vérité unique des règles d'affichage.
 * Utilisée par la grille /courses et par les recommandations du dashboard :
 * toute modification ici se propage aux deux écrans.
 *
 * `course` est une entrée du catalogue (lib/courses-catalog.js). `level`
 * accepte la clé brute (beginner/intermediate/advanced) ou un libellé déjà
 * traduit. `imageIsCover` signale une image non carrée (cover backend) à
 * rendre en vignette recadrée plutôt qu'en icône.
 */
export default function CourseCard({ course }) {
    const t = useTranslations();
    const locale = useLocale();

    const levelLabels = {
        beginner: t("course_beginner"),
        intermediate: t("course_intermediate"),
        advanced: t("course_advanced"),
    };
    const levelLabel = levelLabels[course.level] || course.level;
    const durationLabel = formatCourseDuration(course, t);

    return (
        <Link href={`/course/${course.slug}`} className="h-full">
            <Card className="bg-white dark:bg-bgDark-840 hover:bg-gray-50 dark:hover:bg-bgDark border-gray-200 dark:border-borderDark transition-all duration-200 h-full flex flex-col">
                <CardHeader className="pb-3 flex-1">
                    <div className="flex justify-between items-start">
                        {!course.image ? (
                            <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                                <span className="text-white font-bold text-lg">
                                    {(course.title || "?").charAt(0)}
                                </span>
                            </div>
                        ) : course.imageIsCover ? (
                            <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
                                <Image
                                    src={course.image}
                                    alt={course.title}
                                    width={64}
                                    height={64}
                                    className="object-cover w-full h-full"
                                />
                            </div>
                        ) : (
                            <Image
                                src={course.image}
                                alt={course.title}
                                width={64}
                                height={64}
                            />
                        )}
                        {course.slug === "formation-html" ||
                        course.slug === "formation-javascript" ? (
                            <div className="flex flex-col items-center gap-1.5">
                                <Badge variant="highlight">
                                    {t("course_interactive_challenges")}
                                </Badge>
                                <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-500 dark:text-grayDark-400 mb-0 text-center">
                                    {t("course_new_version", {
                                        year: 2026,
                                    })}
                                </p>
                            </div>
                        ) : course.isSoon && course.soonLabelKey ? (
                            // Échéance annoncée sous le badge « À venir » : la clé
                            // vient du catalogue (soonLabelKey), une formation à
                            // venir sans échéance retombe sur le badge seul.
                            <div className="flex flex-col items-end gap-1.5">
                                <Badge>{t("course_soon")}</Badge>
                                <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-500 dark:text-grayDark-400 mb-0 text-right">
                                    {t(course.soonLabelKey)}
                                </p>
                            </div>
                        ) : (
                            <div className="flex gap-2 flex-wrap">
                                {course.isPopular && (
                                    <Badge>{t("course_popular")}</Badge>
                                )}
                                {course.isSoon && (
                                    <Badge>{t("course_soon")}</Badge>
                                )}
                                {course.isNew && (
                                    <Badge variant="confidence">
                                        {t("course_new")}
                                    </Badge>
                                )}
                                {course.lang && course.lang !== locale && (
                                    <Badge variant="outline">
                                        {t("course_not_same_lang_short")}
                                    </Badge>
                                )}
                            </div>
                        )}
                    </div>
                    <CardTitle className="pt-4 leading-5">
                        <h2>{course.title}</h2>
                    </CardTitle>
                    <CardDescription className="line-clamp-4">
                        {course.description}
                    </CardDescription>
                </CardHeader>
                <CardContent className="px-3">
                    <div className="flex items-center gap-1">
                        {levelLabel && (
                            <Badge variant="secondary">{levelLabel}</Badge>
                        )}
                        {levelLabel && durationLabel && (
                            <span className="text-sm text-muted-foreground">
                                •
                            </span>
                        )}
                        {durationLabel && (
                            <Badge variant="secondary">
                                {durationLabel}
                            </Badge>
                        )}
                    </div>
                </CardContent>
            </Card>
        </Link>
    );
}
